import * as Yup from 'yup'
import { AvailablePlanExtra } from '@dtx-company/inter-app/src/types/AvailablePlanExtra'
import { FC, Fragment, useEffect, useMemo } from 'react'
import { MarketingPageRoutes } from '@dtx-company/true-common/src/constants/routes'
import { NumberDropdown } from './NumberDropdown'
import {
  Plan,
  PlanExtra,
  PlanExtraDetail,
  PlanExtraRender,
  PlanExtraType,
  ProFlexBasicAddOns
} from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { UseFormReturn, useForm } from 'react-hook-form'
import { getPriceText } from '../../../utils/getPriceText'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { yupResolver } from '@hookform/resolvers/yup'
import events from '@dtx-company/inter-app/src/event-tracking/events/proConversion'
import { Banner, Box, Checkbox, Link, Stack, Typography } from '@dtx-company/design-system/src'
import Image from 'next/image'

export enum PlanExtraFormFields {
  BUSINESS_NAME = 'businessName',
  PLAN_EXTRA_DETAILS = 'planExtraDetails'
}
export type PlanExtrasForm = {
  businessName: string
  planExtraDetails?: {
    [key in PlanExtraType]?: PlanExtraDetail
  }
}

function getSeatsText(
  currentPlanExtraDetails: PlanExtraDetail[] | undefined,
  availablePlanExtras: AvailablePlanExtra[] | undefined
): string {
  const current = currentPlanExtraDetails?.find(
    detail => detail.planExtra?.planExtraType === 'seats'
  )
  if (current) {
    return `Number of current seats: ${current.quantity}`
  }

  const available = availablePlanExtras?.find(extra => extra.planExtra?.planExtraType === 'seats')
  if (available) {
    return `Included seats: ${available.minQuantity}`
  }

  return ''
}

export interface UsePlanExtrasFormReturn {
  handlePlanExtras: (planExtraType: PlanExtraType, quantity: number) => void
  handleBusinessName: (value: string) => void
  planExtraHeader: string
  formValues: PlanExtrasForm
  planExtraRender: PlanExtraRender[] | undefined
  showContactSalesCard: boolean
  errors?: {
    [K in PlanExtraFormFields]?: {
      message: string
    }
  }
  form: UseFormReturn<PlanExtrasForm>
}

export interface UsePlanExtrasFormOptions {
  planExtras?: PlanExtra[]
  currentPlanExtraDetails?: PlanExtraDetail[]
  availablePlanExtras: AvailablePlanExtra[] | undefined
}

export function usePlanExtrasForm({
  planExtras,
  currentPlanExtraDetails,
  availablePlanExtras
}: UsePlanExtrasFormOptions): UsePlanExtrasFormReturn {
  const authState = useAuthState()

  const currentQuantityById = useMemo(
    () =>
      currentPlanExtraDetails?.reduce<{ [key: string]: number }>((obj, detail) => {
        const { planExtraId, quantity } = detail
        return { ...obj, [planExtraId]: quantity }
      }, {}),
    [currentPlanExtraDetails]
  )

  const filteredPlanExtras = planExtras?.filter(({ planExtraId, maxQuantity }) => {
    return currentQuantityById?.[planExtraId] !== maxQuantity
  })

  const planExtraDetails = useMemo(
    () =>
      availablePlanExtras &&
      Object.fromEntries(
        availablePlanExtras.map(extra => {
          const planExtraType = extra.planExtra?.planExtraType
          const planExtraId = extra.planExtraId
          const quantity = extra.minQuantity
          return [planExtraType, { planExtraId, quantity }]
        })
      ),
    [availablePlanExtras]
  )

  const form = useForm<PlanExtrasForm>({
    mode: 'onTouched',
    defaultValues: {
      ...(!currentQuantityById && { businessName: authState.org?.name || '' }),
      ...(planExtras && {
        planExtraDetails
      })
    },
    resolver: yupResolver(
      Yup.object({
        ...(!currentQuantityById && {
          businessName: Yup.string().required('Name is required').min(3, 'too short')
        })
      })
    )
  })

  const formValues = form.watch()
  useEffect(() => {
    !currentQuantityById && form.register('businessName')
    planExtras && form.register('planExtraDetails')
  }, [currentQuantityById, planExtras, form])

  useEffect(() => {
    if (!planExtraDetails) return
    form.setValue('planExtraDetails', planExtraDetails)
  }, [planExtraDetails, form])

  const handleBusinessName = (value: string): void => {
    events.Revenue_PaymentForm_Selected({ field: 'businessName', value })
    form.setValue('businessName', value)
    form.trigger()
  }

  const handlePlanExtras = (planExtraType: PlanExtraType, quantity: number): void => {
    const { planExtraId } = formValues.planExtraDetails?.[planExtraType] || {}
    form.trigger()
    form.setValue('planExtraDetails', {
      ...formValues.planExtraDetails,
      ...{ [planExtraType]: { planExtraId, quantity } }
    })
  }

  const planExtraRender = availablePlanExtras
    ? availablePlanExtras
        .reduce<PlanExtraRender[]>((allRenders, availablePlanExtra) => {
          if (!availablePlanExtra.planExtra) return allRenders

          const { planExtraType, unitPriceInCents } = availablePlanExtra.planExtra
          const { minQuantity, maxQuantity } = availablePlanExtra
          const { priceText, priceTextYearly, ...rest } = ProFlexBasicAddOns[planExtraType]
          const maxValue = maxQuantity
          const minValue = minQuantity
          const currentValue = formValues.planExtraDetails?.[planExtraType]?.quantity || 0

          return [
            ...allRenders,
            {
              planExtraType,
              currentValue,
              minValue,
              maxValue,
              // Seems like when this was made it reused a type but changed the value
              // so it's a bit odd - something else downstream will have to decide which
              // one of these to use since one is wrong
              priceText: getPriceText({ priceInCents: unitPriceInCents }) + priceText,
              priceTextYearly: getPriceText({ priceInCents: unitPriceInCents }) + priceTextYearly,
              ...rest
            }
          ]
        }, [])
        .sort((extraA, extraB) => -extraA.planExtraType.localeCompare(extraB.planExtraType))
    : undefined

  const errors: UsePlanExtrasFormReturn['errors'] = {
    ...(form.formState.errors.businessName?.message && {
      [PlanExtraFormFields.BUSINESS_NAME]: {
        message: form.formState.errors.businessName?.message
      }
    })
  }

  const showContactSalesCard = filteredPlanExtras?.[0]?.planExtraType !== 'seats'
  const seatHeader = getSeatsText(currentPlanExtraDetails, availablePlanExtras)

  return {
    handlePlanExtras,
    handleBusinessName,
    formValues,
    planExtraRender,
    planExtraHeader: seatHeader,
    showContactSalesCard,
    ...(Boolean(planExtras?.length) && { errors }),
    form
  }
}

export interface PlanExtrasProps {
  planExtraHeader: UsePlanExtrasFormReturn['planExtraHeader']
  planExtraRender: UsePlanExtrasFormReturn['planExtraRender']
  showContactSalesCard: UsePlanExtrasFormReturn['showContactSalesCard']
  handlePlanExtras: UsePlanExtrasFormReturn['handlePlanExtras']
  selectedPlan: Plan
}

export const ContactSalesCard = (): JSX.Element => {
  return (
    <Banner
      description={
        <Typography variant="bodySmall">
          You’ve reached the seat limit for this plan. To add more seats, contact our sales team{' '}
          <Link href={MarketingPageRoutes.CONTACT_SALES} target="_blank" variant="bodySmall">
            here
          </Link>{' '}
          to book an Enterprise demo.
        </Typography>
      }
    />
  )
}

export const PlanExtras: FC<PlanExtrasProps> = ({
  planExtraHeader,
  planExtraRender,
  showContactSalesCard,
  handlePlanExtras,
  selectedPlan
}) => {
  const isYearly = selectedPlan.interval === PlanInterval.YEARLY
  return (
    <Stack>
      {planExtraHeader && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="bodyMedium">{planExtraHeader}</Typography>
        </Box>
      )}
      {showContactSalesCard && (
        <Box sx={{ mt: 2 }}>
          <ContactSalesCard />
        </Box>
      )}
      {Boolean(planExtraRender?.length) && (
        <Box
          sx={{
            mt: 4,
            h4: {
              color: 'text.secondary'
            }
          }}
        >
          <Typography variant="bodySmall" component="h4">
            Add-ons
          </Typography>
        </Box>
      )}
      <form>
        {planExtraRender?.map((extra, _) => {
          const {
            planExtraType,
            isCheckbox,
            currentValue,
            maxValue,
            minValue,
            icon,
            header,
            description
          } = extra

          const priceText = isYearly ? extra.priceTextYearly : extra.priceText
          return (
            <Fragment key={planExtraType}>
              <Box
                sx={theme => ({
                  border: '1px solid',
                  borderColor: 'border.outline',
                  borderRadius: theme.spacing(2),
                  mt: 2,
                  mb: 2,
                  p: 4,
                  display: 'flex',
                  alignItems: 'center'
                })}
              >
                <Box sx={theme => ({ width: isCheckbox ? theme.spacing(6) : theme.spacing(12) })}>
                  {isCheckbox ? (
                    <Checkbox
                      label=""
                      checked={Boolean(currentValue)}
                      onChange={() => {
                        events.Revenue_PaymentForm_Selected({
                          field: planExtraType,
                          value: currentValue
                        })
                        handlePlanExtras(planExtraType, Number(!currentValue))
                      }}
                    />
                  ) : (
                    <NumberDropdown
                      minNumber={minValue}
                      maxNumber={maxValue}
                      currentNumber={currentValue}
                      setNumber={number => {
                        events.Revenue_PaymentForm_Selected({
                          field: planExtraType,
                          value: number
                        })
                        handlePlanExtras(planExtraType, number)
                      }}
                    />
                  )}
                </Box>
                <Stack flexDirection={'column'} width={'100%'} sx={{ ml: 4.5 }} gap={3}>
                  <Stack
                    width={'100%'}
                    gap={4}
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'center'} gap={1}>
                      <Image src={icon} width={24} height={24} alt="add-on icon" />
                      <Typography variant="bodySmall" whiteSpace={'nowrap'}>
                        {header}
                      </Typography>
                    </Stack>
                    <Typography variant="bodySmall" textAlign={'center'}>
                      {priceText}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="bodySmall"
                    component="p"
                    textAlign={{ xs: 'center', md: 'left' }}
                  >
                    {description}
                  </Typography>
                </Stack>
              </Box>
            </Fragment>
          )
        })}
      </form>
    </Stack>
  )
}
